/**
 * Camera: smooth follow on the torus (AMD-24), the §11.7 shake envelope, boss intro
 * framing and letterboxing.
 *
 * AMD-24 removed the arena clamp. There is no edge to stop at, so the focus point simply
 * follows the player through the seam: every difference the follow and the lead take is a
 * `wrapDelta`, and the focus is re-canonicalised into `[-100, 100)` afterwards so that
 * everything downstream — the warp centre, the radar plate, the parallax lattice — reads
 * one bounded number. The crossing itself is a normal damped step of a couple of units;
 * nothing in this file can tell it happened.
 *
 * AMD-7 R8's lead is unchanged in behaviour and now measures the player→boss vector the
 * short way round, so a boss five units across the cut pulls the framing five units, not
 * a hundred and ninety-five the other way.
 *
 * The camera is orthographic in world units. `viewHeight` is the vertical extent in
 * units; the horizontal extent follows the aspect, with a floor so a narrow portrait
 * phone still sees enough width to read an incoming flank (AMD-1, 390×844 first).
 */

import * as THREE from 'three';
import { cfg } from '../core/config';
import type { RngStream } from '../core/rng';
import { wrapDeltaX, wrapDeltaY, wrapX, wrapY } from '../sim/wrap';
import { WARP_OUT, warpOffset } from './warp';

/** Vertical extent of the view in world units at the 16:9 reference framing. */
export const BASE_VIEW_HEIGHT = 30;
/**
 * Framing bounds, in world units. A 390×844 portrait phone has an aspect of 0.46, so
 * 30 u of height would show 14 u of width — half an arena lane, and an ambush from the
 * side with no warning. Widening to a 20 u floor trades vertical reach for the lateral
 * awareness the pillar-1 "what will kill me in 2 seconds" promise needs. The height cap
 * stops a very tall viewport from shrinking a 1.4 u enemy below the §7.7 48 px bar.
 */
export const MIN_VIEW_WIDTH = 20;
export const MAX_VIEW_WIDTH = 68;
export const MAX_VIEW_HEIGHT = 42;
/** §11.7: offset = trauma² × 0.5 u max. */
const SHAKE_MAX_OFFSET_DEFAULT = 0.5;
/** §11.7: rotation ≤ 0.5°. */
const SHAKE_MAX_ROT_DEG_DEFAULT = 0.5;
/** Follow stiffness, in e-folds per second. */
const FOLLOW_RATE = 11;
/** Boss intro pulls the framing back by this factor and bars the frame (§13.1.7). */
const CINEMATIC_ZOOM = 1.28;
const LETTERBOX_MAX = 0.11;

/**
 * AMD-7 R8 — the camera leads.
 *
 * A camera pinned to the player put every engagement in the left 60% of the frame and
 * left the right 40% as empty black; in the boss capture the boss centroid sat at 86%
 * of screen width and clipped the edge. The focus point now offsets toward the aim (or
 * motion) vector by 12% of the half-width, and biases toward a live boss until the boss
 * lands no further out than 0.68 of screen width.
 */
const LEAD_FRACTION = 0.12;
/** Total offset budget, as a fraction of the FULL view width in each direction. */
const LEAD_MAX_FRACTION = 0.18;
/** Time constant of the lead's own damping, seconds. */
const LEAD_DAMPING_S = 0.15;

export class GameCamera {
  readonly camera: THREE.OrthographicCamera;

  /** Smoothed focus point, world units. */
  x = 0;
  y = 0;
  viewHeight = BASE_VIEW_HEIGHT;
  viewWidth = BASE_VIEW_HEIGHT;
  /** Fraction of the frame height covered by each bar, 0 when not in a cinematic. */
  letterbox = 0;
  /** §13 reduced-shake toggle: amplitude ×0.25, rotation disabled. */
  reducedShake = false;

  private aspect = 1;
  private shakeX = 0;
  private shakeY = 0;
  private shakeRot = 0;
  private cinematic = 0;
  private primed = false;
  /** AMD-7 R8: the damped lead offset, world units, applied on top of the focus point. */
  private leadX = 0;
  private leadY = 0;

  constructor() {
    this.camera = new THREE.OrthographicCamera(-1, 1, 1, -1, -100, 100);
    this.camera.position.set(0, 0, 10);
    this.camera.updateProjectionMatrix();
  }

  setAspect(aspect: number): void {
    this.aspect = aspect > 0 ? aspect : 1;
  }

  /** Snaps the focus without smoothing. Used on run start and after a scene reset. */
  snapTo(x: number, y: number): void {
    this.x = wrapX(x);
    this.y = wrapY(y);
    this.leadX = 0;
    this.leadY = 0;
    this.primed = true;
  }

  /**
   * AMD-7 R8 — recomputes the lead offset. `aimX/aimY` is the aim or motion direction
   * (need not be normalised); `focusX/focusY` is a live boss centroid, or the player
   * itself when there is no boss. Damped on `dt`, which is sim-derived, so a capture of
   * the same tick reproduces the same framing.
   */
  private updateLead(
    playerX: number,
    playerY: number,
    aimX: number,
    aimY: number,
    focusX: number,
    focusY: number,
    hasFocus: boolean,
    dt: number,
  ): void {
    const halfW = this.viewWidth * 0.5;
    const halfH = this.viewHeight * 0.5;
    let tx = 0;
    let ty = 0;
    const aimLen = Math.hypot(aimX, aimY);
    if (aimLen > 1e-4) {
      tx = (aimX / aimLen) * halfW * LEAD_FRACTION;
      ty = (aimY / aimLen) * halfH * LEAD_FRACTION;
    }
    if (hasFocus) {
      // Half the player→boss vector puts the boss and the player symmetrically about
      // centre, so clamping the offset to 0.18 of the view width lands a distant boss at
      // exactly 0.68 screen width and the player at 0.32.
      //
      // AMD-24: the short way round. A raw subtraction across the seam reverses the sign
      // of the bias and throws the framing at the far side of the arena.
      tx += wrapDeltaX(focusX - playerX) * 0.5;
      ty += wrapDeltaY(focusY - playerY) * 0.5;
    }
    const maxX = this.viewWidth * LEAD_MAX_FRACTION;
    // The 0.68 reasoning above is stated in WIDTH and was applied to height with the same
    // fraction — but the only time the boss bias runs hard is the §13.1.7 intro, and that
    // is exactly when this camera bars off `letterbox` of the frame height at each end.
    // A boss on the ring directly above the player landed at 0.10 of frame height with the
    // top bar covering 0.11 of it: the framing put its own subject behind its own bar, and
    // held it there for the whole three seconds the boss is a ghost. Giving the vertical
    // budget back the height the bars take lands the same boss at 0.20, inside the safe
    // band. `letterbox` is 0 whenever there is no cinematic, so nothing else moves, and it
    // eases with the bars themselves, so the extra budget arrives and leaves with them.
    const maxY = this.viewHeight * (LEAD_MAX_FRACTION + this.letterbox);
    if (tx > maxX) tx = maxX;
    else if (tx < -maxX) tx = -maxX;
    if (ty > maxY) ty = maxY;
    else if (ty < -maxY) ty = -maxY;

    const k = dt > 0 ? 1 - Math.exp(-dt / LEAD_DAMPING_S) : 0;
    this.leadX += (tx - this.leadX) * k;
    this.leadY += (ty - this.leadY) * k;
  }

  /**
   * Advances the camera. `dt` is sim-derived seconds, never wall clock, so a capture
   * of the same tick reproduces the same framing.
   *
   * `trauma` is read from `world.feedback.trauma`; `vfx` is the presentation RNG stream
   * (spine §7 — gameplay streams are never touched here).
   */
  update(
    targetX: number,
    targetY: number,
    trauma: number,
    dt: number,
    arenaHalfW: number,
    arenaHalfH: number,
    cinematicActive: boolean,
    vfx: RngStream,
    aimX = 0,
    aimY = 0,
    focusX = 0,
    focusY = 0,
    hasFocus = false,
  ): void {
    // Framing first: the clamp depends on the view extent.
    this.cinematic += ((cinematicActive ? 1 : 0) - this.cinematic) * (1 - Math.exp(-4 * dt));
    const ease = this.cinematic * this.cinematic * (3 - 2 * this.cinematic);
    const zoom = 1 + (CINEMATIC_ZOOM - 1) * ease;
    this.letterbox = LETTERBOX_MAX * ease;

    let vw = BASE_VIEW_HEIGHT * this.aspect;
    if (vw < MIN_VIEW_WIDTH) vw = MIN_VIEW_WIDTH;
    else if (vw > MAX_VIEW_WIDTH) vw = MAX_VIEW_WIDTH;
    let vh = vw / this.aspect;
    if (vh > MAX_VIEW_HEIGHT) {
      vh = MAX_VIEW_HEIGHT;
      vw = vh * this.aspect;
    }
    this.viewWidth = vw * zoom;
    this.viewHeight = vh * zoom;

    this.updateLead(targetX, targetY, aimX, aimY, focusX, focusY, hasFocus, dt);
    const leadTargetX = targetX + this.leadX;
    const leadTargetY = targetY + this.leadY;

    if (!this.primed) {
      this.x = wrapX(leadTargetX);
      this.y = wrapY(leadTargetY);
      this.primed = true;
    } else {
      const k = 1 - Math.exp(-FOLLOW_RATE * dt);
      // AMD-24: the follow steps along the short arc and is re-canonicalised, so the
      // crossing frame is an ordinary 11-e-folds-per-second step and the stored focus
      // stays inside one fundamental domain for every consumer downstream.
      this.x = wrapX(this.x + wrapDeltaX(leadTargetX - this.x) * k);
      this.y = wrapY(this.y + wrapDeltaY(leadTargetY - this.y) * k);
    }

    // AMD-24 deleted the arena clamp with the walls. `arenaHalfW/H` survive in the
    // signature — `src/game.ts` is not this role's file — and are only consulted for the
    // degenerate case of an arena smaller than the view, which no shipped arena is.
    const halfW = this.viewWidth * 0.5;
    const halfH = this.viewHeight * 0.5;
    if (arenaHalfW <= halfW) this.x = 0;
    if (arenaHalfH <= halfH) this.y = 0;

    // §11.7 trauma model. Offset is quadratic in trauma so small hits barely register
    // and a boss phase change is unmistakable; the caps are the photosafety-adjacent
    // half of the readability guarantee, so they clamp rather than scale.
    const t = Math.min(1, Math.max(0, trauma));
    const amp = this.reducedShake ? 0.25 : 1;
    const maxOffset = cfg.get('feel.shake_max_offset') || SHAKE_MAX_OFFSET_DEFAULT;
    const maxRot = ((cfg.get('feel.shake_max_rot_deg') || SHAKE_MAX_ROT_DEG_DEFAULT) * Math.PI) / 180;
    if (t > 0) {
      const mag = t * t * maxOffset * amp;
      this.shakeX = (vfx.next() * 2 - 1) * mag;
      this.shakeY = (vfx.next() * 2 - 1) * mag;
      this.shakeRot = this.reducedShake ? 0 : (vfx.next() * 2 - 1) * maxRot * t * t;
    } else {
      this.shakeX = 0;
      this.shakeY = 0;
      this.shakeRot = 0;
    }

    const cx = this.x + this.shakeX;
    const cy = this.y + this.shakeY;
    this.camera.left = -halfW;
    this.camera.right = halfW;
    this.camera.top = halfH;
    this.camera.bottom = -halfH;
    this.camera.position.set(cx, cy, 10);
    this.camera.rotation.z = this.shakeRot;
    this.camera.updateProjectionMatrix();
    this.camera.updateMatrixWorld(true);
  }

  /**
   * True when a world-space point is inside the view plus `margin` units.
   *
   * AMD-24: the test runs on the point's WARPED camera-relative offset, which folds in
   * both halves of the transform. The seam wrap is what stops a body at −99 being culled
   * while the camera sits at +99; the curvature term matters in the other direction,
   * because pulling the rim inward brings positions that were outside the flat view into
   * the frame, and a cull that missed them would pop them in at the edge.
   */
  contains(x: number, y: number, margin: number): boolean {
    warpOffset(x, y);
    return (
      Math.abs(WARP_OUT.x) <= this.viewWidth * 0.5 + margin &&
      Math.abs(WARP_OUT.y) <= this.viewHeight * 0.5 + margin
    );
  }

  /** The view's half-diagonal, which is the reference radius the curvature is stated in. */
  get halfDiagonal(): number {
    return Math.hypot(this.viewWidth, this.viewHeight) * 0.5;
  }
}
